import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { validatePasswordConfirmation } from '@/features/account/accountValidation';
import { getAPIErrorRecoveryCopy, getFirstAPIErrorFieldCopy } from '@/lib/api/apiErrorCopy';
import { useAuth } from '@/lib/auth/auth';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { router, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  TextInput,
  View,
} from 'react-native';

export default function ResetPasswordScreen() {
  const { token } = useLocalSearchParams<{ token?: string }>();
  const resetPassword = useAuth((state) => state.resetPassword);
  const isBusy = useAuth((state) => state.isBusy);
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [isComplete, setIsComplete] = React.useState(false);

  async function handleSubmit() {
    setError(null);

    if (!token) {
      setError('Invalid password reset link.');
      return;
    }

    const passwordError = validatePasswordConfirmation(password, confirmPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    try {
      await resetPassword(token, password);
      setIsComplete(true);
    } catch (resetError) {
      setError(
        getFirstAPIErrorFieldCopy(resetError, ['token', 'password']) ??
          getAPIErrorRecoveryCopy(resetError, undefined, 'Unable to reset password.')
      );
    }
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: DESIGN_TOKENS.colors.canvas,
      }}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: 'center',
            padding: 24,
          }}>
          <View
            style={{
              borderRadius: 20,
              borderWidth: 1,
              borderColor: DESIGN_TOKENS.colors.borderStrong,
              backgroundColor: DESIGN_TOKENS.colors.surface,
              padding: 24,
              gap: 16,
            }}>
            <View style={{ gap: 8 }}>
              <Text className="text-[28px] font-bold text-primary">
                Reset password
              </Text>
              <Text variant="muted">
                Choose a new password for your Radious account.
              </Text>
            </View>

            {isComplete ? (
              <View style={{ gap: 16 }}>
                <Text className="text-primary">
                  Your password has been updated. You can sign in now.
                </Text>
                <Button onPress={() => router.replace({ pathname: '/sign-in', params: { mode: 'login' } })}>
                  <Text className="font-semibold text-primary-foreground">
                    Back to sign in
                  </Text>
                </Button>
              </View>
            ) : (
              <>
                <TextInput
                  value={password}
                  onChangeText={setPassword}
                  placeholder="New password"
                  placeholderTextColor={DESIGN_TOKENS.colors.textSoft}
                  secureTextEntry
                  autoComplete="new-password"
                  textContentType="newPassword"
                  autoCapitalize="none"
                  style={{
                    minHeight: 52,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: DESIGN_TOKENS.colors.borderStrong,
                    backgroundColor: DESIGN_TOKENS.colors.canvas,
                    color: DESIGN_TOKENS.colors.text,
                    paddingHorizontal: 16,
                  }}
                />
                <TextInput
                  value={confirmPassword}
                  onChangeText={setConfirmPassword}
                  placeholder="Confirm new password"
                  placeholderTextColor={DESIGN_TOKENS.colors.textSoft}
                  secureTextEntry
                  autoComplete="new-password"
                  textContentType="newPassword"
                  autoCapitalize="none"
                  style={{
                    minHeight: 52,
                    borderRadius: 12,
                    borderWidth: 1,
                    borderColor: DESIGN_TOKENS.colors.borderStrong,
                    backgroundColor: DESIGN_TOKENS.colors.canvas,
                    color: DESIGN_TOKENS.colors.text,
                    paddingHorizontal: 16,
                  }}
                />

                {error ? (
                  <Text className="text-primary">
                    {error}
                  </Text>
                ) : null}

                <Button disabled={isBusy} onPress={() => handleSubmit().catch(() => undefined)}>
                  {isBusy ? (
                    <ActivityIndicator color={DESIGN_TOKENS.colors.textInverse} />
                  ) : (
                    <Text className="font-semibold text-primary-foreground">
                      Update password
                    </Text>
                  )}
                </Button>

                <Pressable onPress={() => router.replace({ pathname: '/sign-in', params: { mode: 'login' } })}>
                  <Text variant="muted" style={{ textAlign: 'center' }}>
                    Back to sign in
                  </Text>
                </Pressable>
              </>
            )}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
